import { useEffect, useState } from 'react'

type Props = {
  open: boolean
  title: string
  description: string
  confirmLabel: string
  onClose: () => void
  onConfirm: (pin: string) => Promise<void>
}

const MAX_DIGITS = 6
const KEYPAD_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9']

export function PinKeypadModal({ open, title, description, confirmLabel, onClose, onConfirm }: Props) {
  const [pin, setPin] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shakeKey, setShakeKey] = useState(0)

  useEffect(() => {
    if (!open) {
      setPin('')
      setError(null)
      setIsSubmitting(false)
    }
  }, [open])

  if (!open) return null

  const canSubmit = pin.length === MAX_DIGITS && !isSubmitting

  function addDigit(digit: string) {
    if (isSubmitting) return
    setPin((prev) => (prev.length >= MAX_DIGITS ? prev : `${prev}${digit}`))
    setError(null)
  }

  function removeDigit() {
    if (isSubmitting) return
    setPin((prev) => prev.slice(0, -1))
    setError(null)
  }

  async function handleSubmit() {
    if (!canSubmit) return
    setIsSubmitting(true)
    setError(null)
    try {
      await onConfirm(pin)
      setPin('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Konfirmasi gagal. Coba lagi.')
      setPin('')
      setShakeKey((prev) => prev + 1)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-inverse-surface/40 flex items-end sm:items-center justify-center p-4">
      <div className="w-full max-w-md bg-surface-card border border-outline-variant rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-variant flex items-center justify-between">
          <p className="text-headline-md text-primary">{title}</p>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-on-surface-variant"
            aria-label="Tutup"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-body-sm text-on-surface-variant text-center">{description}</p>

          <div key={shakeKey} className="flex justify-center gap-2 pin-shake" aria-label="PIN indicator">
            {Array.from({ length: MAX_DIGITS }).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full border border-outline ${
                  i < pin.length ? 'bg-primary border-primary' : 'bg-surface'
                }`}
              />
            ))}
          </div>

          {error && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{error}</span>
            </div>
          )}

          <div className={`grid grid-cols-3 gap-2 transition-opacity ${isSubmitting ? 'opacity-60' : 'opacity-100'}`}>
            {KEYPAD_DIGITS.map((digit) => (
              <button
                key={digit}
                type="button"
                disabled={isSubmitting}
                onClick={() => addDigit(digit)}
                className="pin-key"
                aria-label={`Angka ${digit}`}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={removeDigit}
              className="pin-key"
              aria-label="Hapus digit terakhir"
            >
              <span className="material-symbols-outlined text-xl">backspace</span>
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => addDigit('0')}
              className="pin-key"
              aria-label="Angka 0"
            >
              0
            </button>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="pin-key bg-primary text-on-primary font-semibold disabled:opacity-40"
              aria-label={confirmLabel}
            >
              {isSubmitting ? (
                <span className="material-symbols-outlined text-xl animate-spin">progress_activity</span>
              ) : (
                <span className="material-symbols-outlined text-xl">check</span>
              )}
            </button>
          </div>

          <button type="button" className="btn-outline w-full" onClick={onClose} disabled={isSubmitting}>
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}
